import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
import { ThemeProvider, useTheme } from './ThemeContext';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import StatsBar from './components/StatsBar';
import TakeActionSection from './components/TakeActionSection';
import CampusesSection from './components/CampusesSection';
import CustomPlatformSection from './components/CustomPlatformSection';
import EmpoweringSection from './components/EmpoweringSection';
import FeaturesSection from './components/FeaturesSection';
import TwoPathsSection from './components/TwoPathsSection';
import StudentWinsSection from './components/StudentWinsSection';
import LastChanceSection from './components/LastChanceSection';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import FixedEnrollButton from './components/FixedEnrollButton';
import UrgencyBanner from './components/UrgencyBanner';
import LegalPage from './components/LegalPage';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import SuccessPage from './components/SuccessPage';
import AdminPanel from './components/AdminPanel';
import AnalyticsPage from './components/AnalyticsPage';
import TelegramChatWidget from './components/TelegramChatWidget';
import MatrixLandingPage from './components/MatrixLandingPage';
import OpenBroV2 from './components/OpenBroV2';
import OpenBroV3 from './components/OpenBroV3';
import OpenBroV5 from './components/OpenBroV5';
import OpenBroHome from './components/OpenBroHome';
import OpenBroV20 from './components/OpenBroV20';
import OpenBroV4 from './components/OpenBroV4';
import OpenBroV50 from './components/OpenBroV50';
import OpenBroV51 from './components/OpenBroV51';
import OpenBroV52 from './components/OpenBroV52';
import OpenBroV53 from './components/OpenBroV53';
import OpenBroV60 from './components/OpenBroV60';
import OpenBroV70 from './components/OpenBroV70';
import { CheckoutProvider } from './CheckoutContext';
import CheckoutPortal from './components/CheckoutPortal';
import { LanguageProvider } from './i18n/LanguageContext';
import FormateurPortal from './components/FormateurPortal';
import ProgrammePage from './components/ProgrammePage';
import { assets } from './data/mock';
import { trackPageView } from './lib/tracking';
import VisitBeacon from './components/VisitBeacon';
import FunnelTracker from './components/FunnelTracker';
import ReplayRecorder from './components/ReplayRecorder';
import ActivityRecorder from './components/ActivityRecorder';
import LandingTracker from './components/LandingTracker';
import OpenBroEat from './components/OpenBroEat';

const Home = () => {
  const { isDark } = useTheme();
  return (
    <div className="relative min-h-screen transition-colors duration-500" style={{ backgroundColor: '#020409', backgroundImage: isDark ? 'url(/hero-earth.jpg)' : undefined, backgroundSize: 'cover', backgroundPosition: 'center top', backgroundAttachment: 'fixed' }}>
      {isDark && <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(2,4,9,0.72) 0%, rgba(2,4,9,0.88) 40%, rgba(2,4,9,0.94) 100%)' }} />}
      {isDark && <div className="dark-tech-grid" />}
      <div
        className="block absolute top-0 left-0 w-full pointer-events-none bg-no-repeat bg-cover bg-center z-0 h-screen transition-opacity duration-500"
        style={{ backgroundImage: `url(${assets.bgAlt})`, opacity: isDark ? 0.06 : 0.5, maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)' }}
      >
      </div>
      <main className="overflow-hidden relative z-10">
        <UrgencyBanner />
        <Navbar />
        <HeroSection />
        <StatsBar />
        <TakeActionSection />
        <CampusesSection />
        <CustomPlatformSection />
        <EmpoweringSection />
        <FeaturesSection />
        <TwoPathsSection />
        <StudentWinsSection />
        <LastChanceSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const ConditionalEnrollButton = () => {
  const location = useLocation();
  const hide = location.pathname === '/' || ['/dashboard', '/admin', '/login', '/formateur', '/programme', '/v2', '/v3', '/v5', '/v10', '/v30', '/v4', '/v50', '/v51', '/v52', '/v53', '/v60', '/v70', '/eat', '/beta', '/elite'].some(p => location.pathname.startsWith(p));
  if (hide) return null;
  return <FixedEnrollButton />;
};

const PublicLongSalesPage = () => (
  <>
    <Home />
    <FixedEnrollButton />
    <TelegramChatWidget />
  </>
);

function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [siteUnlocked, setSiteUnlocked] = useState(() => {
    return sessionStorage.getItem('openroom_unlocked') === 'true';
  });

  // Handle bypass key from URL — calls backend securely (not for /admin)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get('key');
    const isAdmin = window.location.pathname.startsWith('/admin');
    const isProgramme = window.location.pathname.startsWith('/programme');
    if (key && !isAdmin && !isProgramme && !user) {
      fetch(`${API}/api/auth/bypass`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
      })
        .then(r => { if (!r.ok) throw new Error('Invalid key'); return r.json(); })
        .then(data => {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          sessionStorage.setItem('openroom_unlocked', 'true');
          setUser(data.user);
          setSiteUnlocked(true);
          window.location.replace('/dashboard');
        })
        .catch(() => { window.history.replaceState({}, '', window.location.pathname); });
    } else if (key && !isAdmin && !isProgramme && user) {
      sessionStorage.setItem('openroom_unlocked', 'true');
      setSiteUnlocked(true);
      window.location.replace('/dashboard');
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleSecretUnlock = useCallback(() => {
    setSiteUnlocked(true);
  }, []);

  // If site is locked, show landing page (but allow /admin, /login, /dashboard routes)
  if (!siteUnlocked) {
    return (
      <div className="App">
        <LanguageProvider>
          <ThemeProvider>
          <CheckoutProvider>
          <BrowserRouter>
            <VisitBeacon />
            <FunnelTracker />
            <ReplayRecorder />
            <ActivityRecorder />
            <LandingTracker />
            <Routes>
              <Route path="/admin/acquisition" element={<Navigate to="/admin/analytics?section=acquisition" replace />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/formateur/:token" element={<FormateurPortal />} />
              <Route path="/programme/:slug" element={<ProgrammePage />} />
              <Route path="/login" element={
                user ? <Navigate to="/dashboard" /> : <LoginPage onLogin={handleLogin} />
              } />
              <Route path="/dashboard" element={
                user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />
              } />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              <Route path="/OpenRoom" element={<PublicLongSalesPage />} />
              <Route path="/openroom" element={<PublicLongSalesPage />} />
              <Route path="/v2" element={<OpenBroV2 />} />
              <Route path="/v5" element={<OpenBroV5 />} />
              <Route path="/v3" element={<OpenBroV3 />} />
              <Route path="/v10" element={<OpenBroHome />} />
              <Route path="/v30" element={<OpenBroV20 />} />
              <Route path="/v4" element={<OpenBroV4 />} />
              <Route path="/v50" element={<OpenBroV50 />} />
              <Route path="/v51" element={<OpenBroV51 />} />
              <Route path="/v52" element={<OpenBroV52 />} />
              <Route path="/v53" element={<OpenBroV53 />} />
              <Route path="/v60" element={<OpenBroV60 />} />
              <Route path="/beta" element={<OpenBroV60 />} />
              <Route path="/elite" element={<OpenBroV60 />} />
              <Route path="/v70" element={<OpenBroV70 />} />
              <Route path="/eat" element={<OpenBroEat home="/eat" />} />
              <Route path="/eatv2" element={<OpenBroEat noMonthly home="/eatv2" />} />
              <Route path="/" element={<OpenBroV60 />} />
              <Route path="*" element={
                <MatrixLandingPage onSecretUnlock={handleSecretUnlock} />
              } />
            </Routes>
            <CheckoutPortal />
          </BrowserRouter>
          </CheckoutProvider>
          </ThemeProvider>
        </LanguageProvider>
      </div>
    );
  }

  return (
    <div className="App">
      <LanguageProvider>
      <ThemeProvider>
        <CheckoutProvider>
        <BrowserRouter>
          <VisitBeacon />
          <FunnelTracker />
          <ReplayRecorder />
          <ActivityRecorder />
          <LandingTracker />
          <PageTracker />
          <Routes>
            <Route path="/" element={<OpenBroV60 />} />
            <Route path="/v10" element={<OpenBroHome />} />
              <Route path="/v30" element={<OpenBroV20 />} />
              <Route path="/v4" element={<OpenBroV4 />} />
              <Route path="/v50" element={<OpenBroV50 />} />
              <Route path="/v51" element={<OpenBroV51 />} />
              <Route path="/v52" element={<OpenBroV52 />} />
              <Route path="/v53" element={<OpenBroV53 />} />
              <Route path="/v60" element={<OpenBroV60 />} />
              <Route path="/beta" element={<OpenBroV60 />} />
              <Route path="/elite" element={<OpenBroV60 />} />
              <Route path="/v70" element={<OpenBroV70 />} />
              <Route path="/eat" element={<OpenBroEat home="/eat" />} />
              <Route path="/eatv2" element={<OpenBroEat noMonthly home="/eatv2" />} />
            <Route path="/OpenRoom" element={<Home />} />
            <Route path="/openroom" element={<Home />} />
            <Route path="/v2" element={<OpenBroV2 />} />
            <Route path="/v3" element={<OpenBroV3 />} />
            <Route path="/v5" element={<OpenBroV5 />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/admin/acquisition" element={<Navigate to="/admin/analytics?section=acquisition" replace />} />
            <Route path="/admin/analytics" element={<AnalyticsPage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/formateur/:token" element={<FormateurPortal />} />
            <Route path="/programme/:slug" element={<ProgrammePage />} />
            <Route
              path="/login"
              element={
                user ? <Navigate to="/dashboard" /> : <LoginPage onLogin={handleLogin} />
              }
            />
            <Route
              path="/dashboard"
              element={
                user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />
              }
            />
          </Routes>
          {!user && <ConditionalEnrollButton />}
          <TelegramChatWidget />
          <CheckoutPortal />
        </BrowserRouter>
        </CheckoutProvider>
      </ThemeProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
